import { IconButton, Scrollbar } from "@carbon6/c6-frontend-design";
import { useMemo } from "react";
import { CodeEditor, type CodeMarker } from "@/shared/ui/codeEditor";
import {
  hasUnappliedJson,
  type MappingEditor,
  type MappingEditorState,
} from "../../../feature/mappingEditor";
import { formatMappingJson, readMappingJson, type JsonProblem } from "../../../feature/mappingJson";
import formStyles from "./MappingForm.module.css";
import styles from "./MappingJsonPane.module.css";

const NO_PROBLEMS: readonly JsonProblem[] = [];

function problemsOf(jsonText: string | null): readonly JsonProblem[] {
  if (jsonText === null) {
    return NO_PROBLEMS;
  }
  const read = readMappingJson(jsonText);
  return read.ok ? NO_PROBLEMS : read.problems;
}

function toMarker(problem: JsonProblem): CodeMarker {
  return { ...problem, message: problem.text, severity: "error" };
}

/** 목록의 경로 표기. 문법 오류는 경로가 없고, 값 전체의 문제는 빈 경로다. */
function pathLabel(path: string | null): string | null {
  if (path === null) {
    return null;
  }
  return path === "" ? "(전체)" : path;
}

function statusText(state: MappingEditorState, problemCount: number): string {
  if (problemCount > 0) {
    return `JSON 에 문제가 ${problemCount}개 있습니다. 고친 뒤 적용해 주세요.`;
  }
  if (hasUnappliedJson(state)) {
    return "적용하지 않은 JSON 편집이 있습니다. 적용하면 편집본이 바뀝니다.";
  }
  return state.dirty
    ? "저장하지 않은 변경이 있습니다. JSON 을 고친 뒤 적용하면 편집본이 바뀝니다."
    : "편집본을 JSON 으로 보입니다. 고친 뒤 적용하면 편집본이 바뀝니다.";
}

/**
 * 매핑 보기의 `JSON` 방식 — 편집본을 wire JSON 으로 보이고 고친 텍스트를 적용한다.
 *
 * 텍스트는 편집기 상태(`jsonText`)에 산다. 그래서 보기를 바꾸거나 episode 를 바꿔도 적용하지 않은 편집이
 * 남는다. 문제는 텍스트가 바뀔 때마다 다시 읽는다. 편집기 marker 와 아래 목록이 같은 문제를 보인다.
 * textarea 폴백은 marker 를 그리지 않으므로 목록이 문제를 알리는 자리다.
 */
export function MappingJsonPane({
  editor,
  state,
}: {
  editor: MappingEditor;
  state: MappingEditorState;
}) {
  const formatted = useMemo(() => formatMappingJson(state.draft), [state.draft]);
  const problems = useMemo(() => problemsOf(state.jsonText), [state.jsonText]);
  const markers = useMemo(() => problems.map(toMarker), [problems]);
  const unapplied = hasUnappliedJson(state);
  return (
    <div className={styles.pane}>
      <div className={styles.toolbar}>
        <p className={styles.status} role="status">
          {statusText(state, problems.length)}
        </p>
        <div className={styles.toolbarActions}>
          <IconButton
            disabled={!unapplied}
            text="편집 버리기"
            tone="neutral"
            type="button"
            variant="surface"
            onClick={() => editor.discardJson()}
          />
          <IconButton
            disabled={!unapplied || problems.length > 0}
            text="적용"
            tone="primary"
            type="button"
            variant="solid"
            onClick={() => editor.applyJson()}
          />
        </div>
      </div>
      <CodeEditor
        ariaLabel="매핑 JSON 편집"
        language="json"
        markers={markers}
        value={state.jsonText ?? formatted}
        onChange={(next) => editor.setJsonText(next)}
      />
      {problems.length === 0 ? null : (
        <Scrollbar className={styles.problems} gutter="auto">
          <ul className={styles.problemList} aria-label="JSON 문제">
            {problems.map((problem, index) => {
              const path = pathLabel(problem.path);
              return (
                <li key={index} className={formStyles.error}>
                  <span className={styles.position}>
                    {problem.startLine}줄 {problem.startColumn}열
                  </span>
                  {path === null ? null : <code className={styles.path}>{path}</code>}
                  <span>{problem.text}</span>
                </li>
              );
            })}
          </ul>
        </Scrollbar>
      )}
    </div>
  );
}
